import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import useReveal from "../hooks/useReveal";

const API_URL = `${
  process.env.REACT_APP_BACKEND_URL || "https://shpc-webdev.onrender.com"
}/api`;

const INTERESTS = [
  "Data sovereignty & security compliance",
  "High-performance research computing",
  "AI / ML model development",
  "Climate modeling & environmental simulation",
  "Strategic digital services",
];

const SCALES = [
  "Pilot / Small (up to 100 GPUs)",
  "Medium (100 – 1,000 GPUs)",
  "Large (1,000 – 10,000 GPUs)",
  "Exascale (10,000+ GPUs)",
  "Unsure — need consultation",
];

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    org: "",
    role: "",
    interest: INTERESTS[0],
    scale: SCALES[0],
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.org) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/contact`, form, {
        headers: { "Content-Type": "application/json" },
        timeout: 20000,
      });
      toast.success("Survey submitted — thank you. We'll be in touch.");
      setForm({
        name: "",
        email: "",
        org: "",
        role: "",
        interest: INTERESTS[0],
        scale: SCALES[0],
        notes: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not submit right now. Please try again shortly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full border border-[var(--border-subtle)] px-4 py-3.5 text-[var(--text)] placeholder-[var(--text-30)] text-sm focus:border-[var(--color-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] transition-colors";
  const labelCls =
    "font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--text-50)] mb-2 block";
  const inputStyle = { background: "var(--input-bg)" };

  return (
    <section
      ref={ref}
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 border-t border-[var(--border-faint)]"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="reveal mb-14 text-center">
          <div className="eyebrow mb-4">— 05 / Join the mission</div>
          <h2 className="font-display text-3xl md:text-5xl text-[var(--text)] leading-[1.05] tracking-tight mb-5">
            Shaping a digital future — together.
          </h2>
          <p className="text-[var(--text-60)] text-[15px] leading-relaxed max-w-2xl mx-auto">
            Help us understand how sovereign supercomputing can meet your
            future needs. Your insights guide our development.
          </p>
        </div>

        <form
          data-testid="survey-form"
          onSubmit={submit}
          className="reveal grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8 border border-[var(--border-subtle)] p-8 md:p-12"
          style={{ background: "var(--surface)" }}
        >
          <div>
            <label className={labelCls}>Full Name *</label>
            <input
              data-testid="form-name"
              className={inputCls}
              style={inputStyle}
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Jane Citizen"
            />
          </div>
          <div>
            <label className={labelCls}>Email Address *</label>
            <input
              data-testid="form-email"
              type="email"
              className={inputCls}
              style={inputStyle}
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="jane@org.gov.au"
            />
          </div>
          <div>
            <label className={labelCls}>Organization *</label>
            <input
              data-testid="form-org"
              className={inputCls}
              style={inputStyle}
              value={form.org}
              onChange={(e) => update("org", e.target.value)}
              placeholder="ANU / DSTG / Industry"
            />
          </div>
          <div>
            <label className={labelCls}>Role / Position</label>
            <input
              data-testid="form-role"
              className={inputCls}
              style={inputStyle}
              value={form.role}
              onChange={(e) => update("role", e.target.value)}
              placeholder="Director of AI Research"
            />
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>Primary interest in sovereign computing</label>
            <select
              data-testid="form-interest"
              className={inputCls}
              style={inputStyle}
              value={form.interest}
              onChange={(e) => update("interest", e.target.value)}
            >
              {INTERESTS.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>Scale of computational resources</label>
            <select
              data-testid="form-scale"
              className={inputCls}
              style={inputStyle}
              value={form.scale}
              onChange={(e) => update("scale", e.target.value)}
            >
              {SCALES.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>Your vision / specific needs</label>
            <textarea
              data-testid="form-notes"
              className={inputCls + " min-h-[120px] resize-none"}
              style={inputStyle}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Tell us how sovereign supercomputing could advance your goals..."
            />
          </div>

          <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-40)]">
              Encrypted in transit · Held in AU
            </div>
            <button
              data-testid="form-submit"
              type="submit"
              disabled={submitting}
              className="btn-accent disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting…" : "Submit survey"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
